import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState, useMemo } from "react"
import { Link } from "react-router-dom"
import { Check, Mail as MailIcon, RefreshCw, Phone as PhoneIcon, MessageCircle, Plus, Trash2, Edit, Folder, Users, PenLine, Sparkles, Database } from "lucide-react"
import { api } from "@/lib/api"
import { FilterBar } from "@/components/portal/FilterBar"
import { aufgabeTrifft } from "@/lib/aufgabenSuche"
import { dringlichkeit, erstelltText, faelligText, herkunft, type Dringlichkeit, type HerkunftArt } from "@/lib/aufgabenListe"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { TaskCreateModal } from "@/components/portal/TaskCreateModal"
import { TaskEditModal } from "@/components/portal/TaskEditModal"
import { HeroVorgangName } from "@/components/portal/HeroVorgangName"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { InhaberFrageDialog } from "@/components/portal/InhaberFrageDialog"
import { InhaberFragenSpalte } from "@/components/portal/InhaberFragenSpalte"
import { fragenLaden, type InhaberFrage } from "@/lib/inhaberFragen"
import "./aufgaben.css"

type TaskItem = NonNullable<Awaited<ReturnType<typeof fetchTasks>>>["items"][number]

async function fetchTasks(tenant: string, status: string) {
  const { data, error } = await api.GET("/api/v1/tasks", {
    params: { query: { tenant: tenant || undefined, status: status || undefined, limit: 500 } },
  })
  if (error) throw new Error(String(error))
  return data
}

const HERKUNFT_ICON: Record<HerkunftArt, typeof MailIcon> = {
  mail: MailIcon, anruf: PhoneIcon, whatsapp: MessageCircle, hero: Database, hand: PenLine, ki: Sparkles,
}

type Lage = "" | "ueberfaellig" | "heute" | "ohne_mitarbeiter" | "wartet_inhaber" | "antwort"

/**
 * Aufgaben nach Dringlichkeit (#271, Prototyp 03.11.2026): Überfällig, Heute,
 * Diese Woche, Später, Ohne Fälligkeit — statt einer Tabelle pro Betrieb.
 * Herkunft in Worten, keine HERO-IDs; erledigen per Kreis mit Rückgängig.
 */
export default function TasksList() {
  const [tenant, setTenant] = useTenantFilter()
  const [show, setShow] = useState<"open" | "done">("open")
  const [userFilter, setUserFilter] = useState<string>("")  // "" = alle, "__unassigned__" = ohne
  const [suche, setSuche] = useState("")  // #154: Projekt-Nr., Projekt, Kunde, Titel, Text, Mitarbeiter
  const [lage, setLage] = useState<Lage>("")
  // Paul 03.11.2026: chronologisch (neueste zuerst), Fälligkeit nur auf Wunsch
  const [sortierung, setSortierung] = useState<"neu" | "faellig">("neu")
  const [createOpen, setCreateOpen] = useState(false)
  const [editTask, setEditTask] = useState<TaskItem | null>(null)
  const [weg, setWeg] = useState<number | null>(null)
  const [toast, setToast] = useState<{ text: string; rueckgaengig?: () => void } | null>(null)
  const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  // #271: Fragen an den Inhaber — Dialog fuer eine Aufgabe bzw. eine Frage der Warteschlange
  const [frageFuer, setFrageFuer] = useState<TaskItem | null>(null)
  const [frageBearbeiten, setFrageBearbeiten] = useState<InhaberFrage | null>(null)
  const [aufgabeAusAntwort, setAufgabeAusAntwort] = useState<InhaberFrage | null>(null)
  const [freieFrage, setFreieFrage] = useState(false)
  const qc = useQueryClient()

  // Auto-select wenn nur ein Tenant existiert (wie altes Portal)
  const tenantsDd = useQuery({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => (await api.GET("/api/v1/tenants", {})).data,
    staleTime: 1000 * 60 * 5,
  })
  useEffect(() => {
    const items = tenantsDd.data?.items
    if (!tenant && items && items.length === 1) {
      setTenant(items[0].slug)
    }
  }, [tenant, tenantsDd.data])

  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ["tasks", tenant, show],
    queryFn: () => fetchTasks(tenant, show),
  })

  // Nur mit gewaehltem Betrieb. Fehlt die API (noch nicht ausgerollt), bleibt die Seite wie ohne Spalte.
  const fragenStand = useQuery({
    queryKey: ["inhaber-fragen", tenant],
    queryFn: () => fragenLaden(tenant),
    enabled: !!tenant,
    retry: false,
    staleTime: 30_000,
  })
  const fragen = tenant ? fragenStand.data : undefined
  const frageZuAufgabe = useMemo(() => {
    const m = new Map<number, InhaberFrage>()
    // Offene Frage vor beantworteter vor gesehener — je Aufgabe zählt die aktuellste
    const rang = { warteschlange: 3, gesendet: 3, beantwortet: 2, erledigt: 1 } as const
    for (const f of [...(fragen?.items ?? [])].sort((a, b) => a.id - b.id)) {
      if (f.task_id == null) continue
      const alt = m.get(f.task_id)
      if (!alt || rang[f.status] >= rang[alt.status]) m.set(f.task_id, f)
    }
    return m
  }, [fragen])

  useEffect(() => () => clearTimeout(toastTimer.current), [])
  const zeigeToast = (text: string, rueckgaengig?: () => void) => {
    setToast({ text, rueckgaengig })
    clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(null), 5000)
  }

  // ?tenant= mitgeben, damit das Backend fortytools-Aufgaben (liegen nicht in
  // unserer DB) direkt in fortytools togglen/löschen kann. Für HERO-Tenants
  // ignoriert das Backend den Param und nutzt den lokalen Pfad.
  const tq = tenant ? `?tenant=${encodeURIComponent(tenant)}` : ""
  const setStatus = async (id: number, status: "done" | "open") => {
    const res = await fetch(`/api/v1/tasks/${id}${tq}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      credentials: "include", body: JSON.stringify({ status }),
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.error) throw new Error(json.error || `HTTP ${res.status}`)
  }
  const reopen = useMutation({
    mutationFn: (id: number) => setStatus(id, "open"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
    onError: (e) => zeigeToast(`Wiedereröffnen fehlgeschlagen: ${String((e as Error).message || e)}`),
  })
  const markDone = useMutation({
    mutationFn: (t: TaskItem) => setStatus(t.id, "done"),
    onMutate: (t) => setWeg(t.id),
    onSuccess: (_d, t) => {
      zeigeToast(`Erledigt: ${t.title || "(ohne Titel)"}`, () => reopen.mutate(t.id))
      return qc.invalidateQueries({ queryKey: ["tasks"] })
    },
    onError: (e) => zeigeToast(`Erledigen fehlgeschlagen: ${String((e as Error).message || e)}`),
    onSettled: () => setWeg(null),
  })

  const del = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/v1/tasks/${id}${tq}`, {
        method: "DELETE", credentials: "include", redirect: "manual",
      })
      if (res.ok || res.type === "opaqueredirect") return
      throw new Error(`HTTP ${res.status}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
    onError: (e) => zeigeToast(`Löschen fehlgeschlagen: ${String((e as Error).message || e)}`),
  })

  const allItems = data?.items ?? []

  // Mitarbeiter-Zaehler (nur OFFENE Aufgaben, unabhaengig vom show-Filter).
  const userStats = useMemo(() => {
    const open = allItems.filter((t) => t.status === "open")
    const by: Record<string, number> = {}
    let unassigned = 0
    for (const t of open) {
      const n = (t.target_user_name || "").trim()
      if (!n) unassigned++
      else by[n] = (by[n] || 0) + 1
    }
    const list = Object.entries(by).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    return { list, unassigned, total: open.length }
  }, [allItems])

  // Lageleiste: zählt die offenen Aufgaben vor Mitarbeiter-Filter und Suche.
  const lageZahlen = useMemo(() => {
    const open = allItems.filter((t) => t.status === "open")
    return {
      ueberfaellig: open.filter((t) => dringlichkeit(t.due_date) === "ueberfaellig").length,
      heute: open.filter((t) => dringlichkeit(t.due_date) === "heute").length,
      ohne_mitarbeiter: open.filter((t) => !(t.target_user_name || "").trim()).length,
      wartet_inhaber: (fragen?.items ?? []).filter((f) => f.status === "warteschlange" || f.status === "gesendet").length,
      antwort: (fragen?.items ?? []).filter((f) => f.status === "beantwortet").length,
    }
  }, [allItems, fragen])

  const items = useMemo(() => {
    let liste = allItems
    if (userFilter === "__unassigned__") liste = liste.filter((t) => !(t.target_user_name || "").trim())
    else if (userFilter) liste = liste.filter((t) => (t.target_user_name || "").trim() === userFilter)
    if (show === "open" && lage === "ohne_mitarbeiter") liste = liste.filter((t) => !(t.target_user_name || "").trim())
    else if (show === "open" && lage === "wartet_inhaber") liste = liste.filter((t) => { const f = frageZuAufgabe.get(t.id); return !!f && (f.status === "warteschlange" || f.status === "gesendet") })
    else if (show === "open" && lage === "antwort") liste = liste.filter((t) => frageZuAufgabe.get(t.id)?.status === "beantwortet")
    else if (show === "open" && lage) liste = liste.filter((t) => dringlichkeit(t.due_date) === lage)
    return suche.trim() ? liste.filter((t) => aufgabeTrifft(t, suche)) : liste
  }, [allItems, userFilter, suche, lage, show, frageZuAufgabe])

  const erstelltAm = (t: TaskItem) => (t as { hero_created?: string | null }).hero_created || t.created_at || ""
  const sortiert = useMemo(() => {
    const liste = [...items]
    if (sortierung === "faellig") {
      // Ohne Fälligkeit ans Ende, sonst früheste zuerst
      return liste.sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999") || b.id - a.id)
    }
    return liste.sort((a, b) => erstelltAm(b).localeCompare(erstelltAm(a)) || b.id - a.id)
  }, [items, sortierung])

  const mehrereBetriebe = !tenant && new Set(allItems.map((t) => t.tenant_slug || "")).size > 1

  if (error) return <div className="pp-main"><ErrorBox error={error} /></div>

  return (
    <>
      <div className="pp-page-header">
        <h2>Aufgaben <span className="page-count">({items.length})</span></h2>
        <div style={{ display: "inline-flex", gap: 8 }}>
          <button className="pp-btn pp-btn-sm" onClick={() => refetch()} disabled={isFetching}>
            Aktualisieren
          </button>
          <button className="pp-btn pp-btn-primary" onClick={() => setCreateOpen(true)}>
            <Plus className="w-3.5 h-3.5" /> Neue Aufgabe
          </button>
        </div>
      </div>

      <FilterBar tenant={tenant} onTenantChange={setTenant}>
        <div style={{ display: "inline-flex", gap: 4, marginLeft: 8 }}>
          {(["open", "done"] as const).map((s) => (
            <button
              key={s}
              onClick={() => { setShow(s); if (s === "done") setLage("") }}
              className={s === show ? "pp-btn pp-btn-primary pp-btn-sm" : "pp-btn pp-btn-sm"}
            >
              {s === "open" ? "Offen" : "Erledigt"}
            </button>
          ))}
        </div>
        <div className="ag-filterzeile">
        <select
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
          style={{ marginLeft: 8, fontSize: 13, padding: "4px 8px" }}
          title="Mitarbeiter-Filter (zeigt offene Aufgaben pro Mitarbeiter)"
          aria-label="Mitarbeiter"
        >
          <option value="">Alle Mitarbeiter ({userStats.total} offen)</option>
          {userStats.list.map(([name, cnt]) => (
            <option key={name} value={name}>{name} ({cnt} offen)</option>
          ))}
          {userStats.unassigned > 0 && (
            <option value="__unassigned__">— Ohne Mitarbeiter ({userStats.unassigned} offen) —</option>
          )}
        </select>
        <select
          value={show === "open" ? lage : ""}
          onChange={(e) => { setShow("open"); setLage(e.target.value as Lage) }}
          style={{ marginLeft: 8, fontSize: 13, padding: "4px 8px" }}
          aria-label="Anzeigen"
          disabled={show !== "open"}
        >
          <option value="">Alle offenen</option>
          <option value="ueberfaellig">Überfällig ({lageZahlen.ueberfaellig})</option>
          <option value="heute">Heute fällig ({lageZahlen.heute})</option>
          <option value="ohne_mitarbeiter">Ohne Mitarbeiter ({lageZahlen.ohne_mitarbeiter})</option>
          {fragen && <option value="wartet_inhaber">Warten auf Inhaber ({lageZahlen.wartet_inhaber})</option>}
          {fragen && <option value="antwort">Neue Antworten vom Inhaber ({lageZahlen.antwort})</option>}
        </select>
        <select
          value={sortierung}
          onChange={(e) => setSortierung(e.target.value as "neu" | "faellig")}
          style={{ marginLeft: 8, fontSize: 13, padding: "4px 8px" }}
          aria-label="Sortierung"
        >
          <option value="neu">Neueste zuerst</option>
          <option value="faellig">Nach Fälligkeit</option>
        </select>
        </div>
        <input
          type="search"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          placeholder="Suchen: Projekt-Nr., Kunde, Titel …"
          aria-label="Aufgaben suchen"
          style={{ marginLeft: 8, fontSize: 13, padding: "4px 8px", minWidth: 220 }}
        />
      </FilterBar>

      <TaskCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultTenant={tenant}
      />
      <TaskEditModal
        open={!!editTask}
        onClose={() => setEditTask(null)}
        task={editTask}
      />
      {(frageFuer || frageBearbeiten || freieFrage) && (
        <InhaberFrageDialog
          frei={freieFrage ? tenant : null}
          aufgabe={frageFuer ? { id: frageFuer.id, title: frageFuer.title || "", vorgang: [frageFuer.project_nr, frageFuer.project_title].filter(Boolean).join(" · ") } : null}
          bestehend={frageBearbeiten}
          inhaber={fragen?.inhaber ?? null}
          onClose={() => { setFrageFuer(null); setFrageBearbeiten(null); setFreieFrage(false) }}
        />
      )}
      {/* Aus einer Antwort wird nur auf Klick eine Aufgabe — nie automatisch (B9). */}
      <TaskCreateModal
        open={!!aufgabeAusAntwort}
        onClose={() => setAufgabeAusAntwort(null)}
        defaultTenant={tenant}
        titel="Aufgabe aus der Antwort anlegen"
        initial={aufgabeAusAntwort ? {
          title: aufgabeAusAntwort.vorgang ? `${aufgabeAusAntwort.vorgang}: nach Antwort des Inhabers` : "Nach Antwort des Inhabers",
          comment: `Frage: ${aufgabeAusAntwort.frage}\nAntwort: ${aufgabeAusAntwort.antwort_text || ""}`,
        } : undefined}
      />

      <div className={`ag-spalten${fragen ? " mit-fragen" : ""}`}>
      <div style={{ minWidth: 0 }}>
      {isLoading ? (
        <Loading label="Lädt Aufgaben..." />
      ) : sortiert.length === 0 ? (
        <div className="pp-card ag-leer-hinweis">
          {show === "open" ? (lage || suche || userFilter ? "Nichts offen in dieser Auswahl." : "Keine offenen Aufgaben.") : "Keine erledigten Aufgaben."}
        </div>
      ) : (
        <div className="pp-card" style={{ padding: 0 }}>
          <div className="pp-tbl-wrap ag-tabelle" style={{ border: "none", boxShadow: "none", margin: 0 }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 74 }}>Erstellt</th>
                  {mehrereBetriebe && <th style={{ width: 90 }}>Betrieb</th>}
                  <th>Aufgabe</th>
                  <th style={{ width: "17%" }}>Vorgang / Kunde</th>
                  <th style={{ width: 104 }}>Herkunft</th>
                  <th style={{ width: 100 }}>Mitarbeiter</th>
                  <th style={{ width: 84 }}>Fällig</th>
                  <th style={{ width: 96 }} aria-label="Aktion" />
                </tr>
              </thead>
              <tbody>
                {sortiert.map((t) => (
                  <AufgabenReihe
                    key={t.id}
                    t={t}
                    erledigt={show === "done"}
                    weg={weg === t.id}
                    betriebZeigen={mehrereBetriebe}
                    onErledigt={() => markDone.mutate(t)}
                    onWiederoeffnen={() => reopen.mutate(t.id)}
                    onBearbeiten={() => setEditTask(t)}
                    onLoeschen={() => { if (confirm(`Aufgabe "${t.title}" löschen?`)) del.mutate(t.id) }}
                    beschaeftigt={markDone.isPending || reopen.isPending}
                    loeschtGerade={del.isPending}
                    frage={frageZuAufgabe.get(t.id)}
                    onInhaberFragen={fragen && show === "open" ? () => setFrageFuer(t) : undefined}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>
      {fragen && (
        <InhaberFragenSpalte stand={fragen} tenant={tenant} onNeueFrage={() => setFreieFrage(true)}
          onBearbeiten={setFrageBearbeiten} onAufgabeAnlegen={setAufgabeAusAntwort} />
      )}
      </div>

      {toast && (
        <div className="ag-toast" role="status">
          <span>{toast.text}</span>
          {toast.rueckgaengig && (
            <button onClick={() => { toast.rueckgaengig!(); setToast(null) }}>Rückgängig</button>
          )}
        </div>
      )}
    </>
  )
}

function AufgabenReihe({
  t, erledigt, weg, betriebZeigen, onErledigt, onWiederoeffnen, onBearbeiten, onLoeschen, beschaeftigt, loeschtGerade,
  frage, onInhaberFragen,
}: {
  t: TaskItem
  erledigt: boolean
  weg: boolean
  betriebZeigen: boolean
  onErledigt: () => void
  onWiederoeffnen: () => void
  onBearbeiten: () => void
  onLoeschen: () => void
  beschaeftigt: boolean
  loeschtGerade: boolean
  frage?: InhaberFrage
  /** undefined = Fragen an den Inhaber hier nicht verfügbar */
  onInhaberFragen?: () => void
}) {
  const h = herkunft(t.source)
  const HIcon = HERKUNFT_ICON[h.art]
  const d: Dringlichkeit = dringlichkeit(t.due_date)
  const titel = t.title || "(ohne Titel)"
  const bearbeitbar = t.source !== "fortytools"
  const tq = t.tenant_slug ? `?tenant=${encodeURIComponent(t.tenant_slug)}` : ""
  const kontaktName = (t as { contact_name?: string }).contact_name
  const erstellt = erstelltText((t as { hero_created?: string | null }).hero_created, t.created_at).replace(/^erstellt /, "")

  return (
    <tr className={weg ? "ag-weg" : undefined}>
      <td className="ag-zelle-leise">{erstellt ? <>{erstellt.split(" ")[0]}<br />{erstellt.split(" ")[1]}</> : "—"}</td>
      {betriebZeigen && <td className="ag-zelle-leise">{(t.tenant_slug || "").replace(/_/g, " ")}</td>}
      <td>
        {bearbeitbar ? (
          <button className="ag-titel" onClick={onBearbeiten} title="Bearbeiten">{titel}</button>
        ) : (
          <div className="ag-titel" style={{ cursor: "default" }}>{titel}</div>
        )}
        {t.comment && <AufgabenKommentar text={t.comment} />}
        {!erledigt && (frage || onInhaberFragen) && (
          <div className="ag-frage-zeile">
            {frage?.status === "warteschlange" ? (
              <span><MessageCircle className="w-3 h-3" />Frage an den Inhaber geht als Nächstes raus</span>
            ) : frage?.status === "gesendet" ? (
              <span className={frage.lieber_anrufen || frage.zustellung === "failed" ? "ag-warnung" : undefined}>
                <MessageCircle className="w-3 h-3" />
                {frage.zustellung === "failed" ? "WhatsApp an den Inhaber nicht zugestellt"
                  : frage.lieber_anrufen ? "Inhaber antwortet nicht, lieber anrufen" : "Frage an den Inhaber offen"}
              </span>
            ) : frage?.status === "beantwortet" || frage?.status === "erledigt" ? (
              <span className={frage.status === "beantwortet" ? "ag-antwort" : undefined} title={`Frage: ${frage.frage}`}>
                <Check className="w-3 h-3" />Inhaber: {kurz(frage.antwort_text || "", 80)}
              </span>
            ) : null}
            {(!frage || frage.status === "erledigt") && onInhaberFragen && (
              <button className="ag-frage-link" onClick={onInhaberFragen}>
                <MessageCircle className="w-3 h-3" />Aufgabe per WhatsApp an Inhaber fragen
              </button>
            )}
          </div>
        )}
      </td>
      <td>
        {t.project_match_id ? (
          <a className="ag-link" href={`/admin/open-hero/project/${t.project_match_id}${tq}`} target="_blank" rel="noopener" title="Vorgang in HERO öffnen">
            <Folder className="w-3 h-3" />
            {/* #98: Bezeichnung statt „Projekt #7284194"; fehlt sie, schlaegt die Komponente in HERO nach. */}
            <HeroVorgangName tenantSlug={t.tenant_slug} projectId={t.project_match_id} nr={t.project_nr} titel={t.project_title}
              fallback="Vorgang öffnen" />
          </a>
        ) : null}
        {t.contact_id || kontaktName ? (
          t.contact_id ? (
            <a className="ag-link ag-link-leise" href={`/admin/open-hero/contact/${t.contact_id}${tq}`} target="_blank" rel="noopener" title="Kontakt in HERO öffnen">
              <Users className="w-3 h-3" />{kontaktName || "Kunde öffnen"}
            </a>
          ) : <span className="ag-link-leise"><Users className="w-3 h-3" />{kontaktName}</span>
        ) : null}
        {!t.project_match_id && !t.contact_id && !kontaktName && <span className="ag-zelle-leise">—</span>}
      </td>
      <td className="ag-zelle-leise">
        {h.link ? (
          <Link to={h.link} className="ag-quelle" title="Zur Stelle im Portal, aus der die Aufgabe kommt">
            <HIcon className="w-3 h-3" />{h.text.replace(/^aus /, "")}
          </Link>
        ) : (
          <span className="ag-quelle-text"><HIcon className="w-3 h-3" />{h.text.replace(/^aus /, "")}</span>
        )}
      </td>
      <td style={{ fontSize: 13 }}>{t.target_user_name || <span className="ag-zelle-leise">—</span>}</td>
      <td className={!erledigt && d === "ueberfaellig" ? "ag-ueberfaellig" : !erledigt && d === "heute" ? "ag-heute" : "ag-zelle-leise"}>
        {t.due_date ? faelligText(t.due_date) : "—"}
      </td>
      <td>
        <div className="ag-aktionen">
          {erledigt ? (
            <button className="task-btn task-btn-icon" onClick={onWiederoeffnen} disabled={beschaeftigt}
              title="Wiedereröffnen" aria-label={`Wiedereröffnen: ${titel}`}>
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button className="task-btn task-btn-icon task-btn-primary" onClick={onErledigt} disabled={beschaeftigt || weg}
              aria-label={`Erledigt: ${titel}`} title="Erledigt">
              <Check className="w-3.5 h-3.5" />
            </button>
          )}
          {bearbeitbar && (
            <button className="task-btn task-btn-icon" onClick={onBearbeiten} title="Bearbeiten" aria-label={`Bearbeiten: ${titel}`}>
              <Edit className="w-3.5 h-3.5" />
            </button>
          )}
          <button className="task-btn task-btn-icon task-btn-danger" onClick={onLoeschen} disabled={loeschtGerade}
            title="Löschen" aria-label={`Löschen: ${titel}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  )
}

function kurz(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s
}

/**
 * Beschreibung in der Liste: drei Zeilen, Klick klappt auf (#143).
 * Aeltere Aufgaben tragen teils die ganze Kundenmail — die KI-Kurzfassung
 * greift nur beim Anlegen, die Liste muss auch den Bestand lesbar halten.
 */
function AufgabenKommentar({ text }: { text: string }) {
  const [offen, setOffen] = useState(false)
  const lang = text.length > 240 || text.split("\n").length > 3
  return (
    <div
      onClick={lang ? () => setOffen((o) => !o) : undefined}
      title={lang ? (offen ? "Einklappen" : "Ganze Beschreibung zeigen") : undefined}
      style={{
        fontSize: 12, color: "var(--text-secondary)", marginTop: 2, whiteSpace: "pre-wrap",
        cursor: lang ? "pointer" : undefined,
        ...(lang && !offen
          ? { overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" as const }
          : {}),
      }}
    >
      {text}
    </div>
  )
}
